import { useQuery } from '@tanstack/react-query';
import { DataCard, SkeletonCard, QueryError } from '../../../../components/ui';
import { api } from '../../../../lib/api';

interface CashRunwayResp {
  startingCashCents: number | null;
  weeklyExpenseCents: number;
  weeksUntilCrunch: number | null;
  totalInflow13wCents: number;
  narrative?: string;
}

/** Weeks of runway: crunch point if within the 13-week window, else simple cash/burn. */
function weeksOfRunway(r: CashRunwayResp): number | null {
  if (r.weeksUntilCrunch !== null) return r.weeksUntilCrunch;
  if (r.startingCashCents !== null && r.weeklyExpenseCents > 0) {
    return Math.floor(r.startingCashCents / r.weeklyExpenseCents);
  }
  return null;
}

export function CashRunwayWidget() {
  const cashRunway = useQuery<CashRunwayResp>({
    queryKey: ['cash-runway'],
    queryFn: async () => {
      return api('/v1/cash-runway');
    },
    staleTime: 10 * 60_000,
    retry: 0,
  });

  const r = cashRunway.data;
  const weeks = r ? weeksOfRunway(r) : null;
  const configured = r != null && r.startingCashCents !== null;

  return (
    <DataCard
      title="Cash Runway"
      subtitle="Weeks of runway based on current burn"
      accent={(weeks ?? 99) < 10 ? 'danger' : (weeks ?? 99) < 20 ? 'warn' : 'success'}
    >
      {cashRunway.isLoading ? <SkeletonCard /> : cashRunway.isError ? <QueryError error={cashRunway.error} onRetry={() => cashRunway.refetch()} compact /> : !configured ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-1/50 px-3 py-3 text-center">
          <p className="text-2xs text-ink-dim">
            Add cash on hand + weekly expenses and Waypoint projects your runway week by week.
          </p>
          <a
            href="/dashboard/settings?tab=preferences"
            className="mt-1.5 inline-block rounded-md bg-primary/10 px-2.5 py-1 text-2xs font-semibold text-primary hover:bg-primary/20"
          >
            Configure →
          </a>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-3xl font-semibold tabular-nums text-ink">{weeks !== null ? weeks.toFixed(0) : '—'}</span>
            <span className="text-xs text-ink-dim">weeks</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-2xs">
            <div><span className="text-ink-dim">Cash on hand </span><span className="font-mono text-ink">${Math.round(((r!.startingCashCents ?? 0) / 100) / 1000)}k</span></div>
            <div><span className="text-ink-dim">Weekly burn </span><span className="font-mono text-ink">${Math.round(((r!.weeklyExpenseCents ?? 0) / 100) / 1000)}k</span></div>
          </div>
        </div>
      )}
    </DataCard>
  );
}
